import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChecklistItemDto } from '@rp/contracts';
import { api } from '../lib/api';
import { Button, Card, ScreenTitle, Section, Skeleton } from '../components/ui';
import { haptic } from '../lib/telegram';
import { useSession } from '../hooks/session';

const GROUP_TITLES: Record<ChecklistItemDto['group'], string> = {
  SETUP: 'Настройка',
  REPUTATION: 'Репутация',
  ENGAGEMENT: 'Вовлечённость',
  GROWTH: 'Рост',
};

const GROUP_ORDER: ReadonlyArray<ChecklistItemDto['group']> = ['SETUP', 'REPUTATION', 'ENGAGEMENT', 'GROWTH'];

const SEVERITY_COLOR = {
  CRITICAL: 'var(--color-negative)',
  WARNING: 'var(--color-neutral)',
  INFO: 'var(--color-link)',
} as const;

/** Чек-лист здоровья репутации плюс приоритезированные рекомендации. */
export function ChecklistPage() {
  const { can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['checklist'], queryFn: api.checklist });

  const toggle = useMutation({
    mutationFn: ({ key, done }: { key: string; done: boolean }) => api.toggleChecklist(key, done),
    onSuccess: (data) => {
      haptic.success();
      queryClient.setQueryData(['checklist'], data);
    },
  });

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-2 px-4 pt-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!query.data) {
    return <p className="p-8 text-center text-[var(--color-hint)]">Не удалось загрузить чек-лист</p>;
  }

  const { score, items, recommendations } = query.data;
  const doneCount = items.filter((item) => item.done).length;

  return (
    <div className="mx-auto max-w-lg">
      <ScreenTitle title="Чек-лист" subtitle="Что улучшить в работе с отзывами" />
      <Card className="mb-5 flex items-center gap-4">
        <ScoreRing score={score} />
        <div className="min-w-0">
          <p className="t-headline">
            {score >= 85 ? 'Отличная работа' : score >= 60 ? 'Хороший уровень' : 'Есть что улучшить'}
          </p>
          <p className="mt-0.5 t-footnote text-[var(--color-hint)]">
            Выполнено {doneCount} из {items.length} пунктов
          </p>
        </div>
      </Card>

      {recommendations.length > 0 && (
        <Section title="Что сделать сейчас">
          <div className="space-y-2 px-4">
            {recommendations.map((item) => (
              <div
                key={item.id}
                className="mx-4 rounded-[var(--radius-card)] border-l-[3px] bg-[var(--color-section)] p-4"
                style={{ borderColor: SEVERITY_COLOR[item.severity] }}
              >
                <p className="t-callout font-medium">{item.title}</p>
                <p className="mt-1 t-footnote leading-relaxed text-[var(--color-hint)]">{item.body}</p>
                {item.actionLabel && item.actionRoute && (
                  <Button variant="ghost" className="mt-1.5 -ml-4" onClick={() => navigate(item.actionRoute!)}>
                    {item.actionLabel} →
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {GROUP_ORDER.map((group) => {
        const groupItems = items.filter((item) => item.group === group);
        if (groupItems.length === 0) return null;

        return (
          <Section key={group} title={GROUP_TITLES[group]}>
            <Card className="mx-4 p-0">
              <ul className="divide-y divide-[var(--color-separator)]">
                {groupItems.map((item) => (
                  <li key={item.key} className="p-4">
                    <button
                      className="flex w-full items-start gap-3 text-left disabled:cursor-default"
                      disabled={item.auto || !can.checklist || toggle.isPending}
                      onClick={() => toggle.mutate({ key: item.key, done: !item.done })}
                    >
                      <span
                        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 t-caption text-white"
                        style={{
                          borderColor: item.done ? 'var(--color-positive)' : 'var(--color-separator)',
                          background: item.done ? 'var(--color-positive)' : 'transparent',
                        }}
                        aria-hidden="true"
                      >
                        {item.done ? '✓' : ''}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block t-callout"
                          style={{ color: item.done ? 'var(--color-hint)' : 'var(--color-text)' }}
                        >
                          {item.title}
                        </span>
                        <span className="mt-0.5 block t-footnote leading-relaxed text-[var(--color-hint)]">
                          {item.description}
                        </span>
                        {item.hint && (
                          <span className="mt-1 block t-caption text-[var(--color-neutral)]">{item.hint}</span>
                        )}
                        {item.progress && !item.done && (
                          <span className="mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-[var(--color-surface)]">
                            <span
                              className="block h-full rounded-full bg-[var(--color-accent)]"
                              style={{
                                width: `${Math.min(100, Math.round((item.progress.current / Math.max(item.progress.target, 1)) * 100))}%`,
                              }}
                            />
                          </span>
                        )}
                        {!item.auto && (
                          <span className="mt-1 block t-caption text-[var(--color-hint)]">
                            Отмечается вручную
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </Section>
        );
      })}
    </div>
  );
}

function ScoreRing({ score }: { score: number }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color = score >= 85 ? 'var(--color-positive)' : score >= 60 ? 'var(--color-neutral)' : 'var(--color-negative)';

  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
        <circle cx="40" cy="40" r={radius} fill="none" stroke="var(--color-surface)" strokeWidth="8" />
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference - filled}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center t-title tabular-nums">
        {score}
      </span>
    </div>
  );
}
